import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ArrowLeft, Loader2, Mail, Send } from "lucide-react";

const RadarLogo = ({ className = "h-10 w-auto" }) => (
  <img src="/tabfinder-logo.png" alt="Tab Finder" className={className} />
);

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorText, setErrorText] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    if (!WEB3FORMS_KEY) {
      setErrorText("The contact form is not configured for submissions yet.");
      return;
    }

    const trimmed = {
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim()
    };
    if (!trimmed.name || !trimmed.email || !trimmed.subject || !trimmed.message) {
      setErrorText("Please fill in all fields.");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `[Tab Finder Support] ${trimmed.subject}`,
          name: trimmed.name,
          email: trimmed.email,
          message: trimmed.message,
          from_name: trimmed.name
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Something went wrong. Please try again.");
      }
      setStatus("success");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (err) {
      setErrorText(err.message || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 min-w-0">
              <RadarLogo className="h-9 w-auto shrink-0" />
            </Link>
            <Button variant="outline" size="sm" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 max-w-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">Contact support</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Questions about Tab Finder, billing, or the Chrome extension—we respond as soon as we can.
            </p>
          </div>
        </div>

        {!WEB3FORMS_KEY && (
          <Alert className="mb-6 border-amber-500/50 bg-amber-500/5">
            <AlertTitle className="text-amber-950 dark:text-amber-100">Form not connected</AlertTitle>
            <AlertDescription className="text-amber-900/90 dark:text-amber-100/90">
              Add a free Web3Forms access key to your build: create{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">frontend/.env</code> with{" "}
              <code className="rounded bg-muted px-1 py-0.5 text-xs">REACT_APP_WEB3FORMS_ACCESS_KEY=…</code>{" "}
              (see <code className="rounded bg-muted px-1 py-0.5 text-xs">frontend/.env.example</code>
              ), then rebuild and redeploy.
            </AlertDescription>
          </Alert>
        )}

        {status === "success" && (
          <Alert className="mb-6 border-emerald-500/50 bg-emerald-500/5">
            <AlertTitle className="text-emerald-950 dark:text-emerald-100">Message sent</AlertTitle>
            <AlertDescription className="text-emerald-900/90 dark:text-emerald-100/90">
              Thanks for reaching out. If you need anything else, you can send another message below.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Send a message</CardTitle>
            <CardDescription>Include your browser and extension version if you&apos;re reporting a bug.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input
                  id="contact-name"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={status === "submitting"}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === "submitting"}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input
                  id="contact-subject"
                  name="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={status === "submitting"}
                  placeholder="e.g. Extension won’t sign in"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={status === "submitting"}
                  rows={6}
                  placeholder="Describe what happened and what you expected…"
                  required
                  className="resize-y min-h-[140px]"
                />
              </div>

              {errorText && (
                <p className="text-sm text-destructive" role="alert">
                  {errorText}
                </p>
              )}

              <Button type="submit" className="w-full sm:w-auto gap-2" disabled={status === "submitting" || !WEB3FORMS_KEY}>
                {status === "submitting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Send message
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-8">
          <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">
            Privacy policy
          </Link>
        </p>
      </main>

      <footer className="border-t border-border py-8 mt-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground transition-colors">
            ← Back to home
          </Link>
          <span>© {new Date().getFullYear()} Tab Finder</span>
        </div>
      </footer>
    </div>
  );
}
